/**
 * DisbursementsOnlyPicker — the invoice request's Disbursements step when
 * the billing basis is "Disbursements only" (27 Oct 2026, May Ann: invoice
 * disbursements without the full request).
 *
 * Lists the matter's unbilled, chargeable disbursements, all ticked. Unticking
 * one records its id in excluded_disbursement_ids; the draft invoice made
 * from the request (hub.invoices.create_draft_invoice_from_request) bills the
 * rest. Excluded rather than selected, so LawBillity rows picked on the same
 * step, which are recorded on submit, are billed too.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "../config/api";
import { Checkbox } from "./ui/checkbox";

const VAT_LABEL = { no_vat: "No VAT", plus_vat: "+ VAT", inc_vat: "Inc VAT" };

export function unbilled(rows) {
  return (rows || []).filter(
    (d) => d.chargeable !== false && !["billed", "void", "written_off"].includes(d.status),
  );
}

export default function DisbursementsOnlyPicker({ matterRef, excluded, onChange }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!matterRef) return undefined;
    let cancelled = false;
    const email = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
    axios
      .get(`${API}/disbursements`, { params: { matter_ref: matterRef }, headers: { "X-User-Email": email } })
      .then(({ data }) => { if (!cancelled) setRows(unbilled(data.disbursements)); })
      .catch(() => { if (!cancelled) { setRows([]); setError("Could not load this matter's disbursements."); } });
    return () => { cancelled = true; };
  }, [matterRef]);

  const off = new Set(excluded || []);
  const toggle = (id) => {
    const next = new Set(off);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };

  if (rows === null) {
    return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading disbursements</div>;
  }
  return (
    <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50/40 space-y-2" data-testid="disb-only-picker">
      <p className="text-sm font-medium text-slate-700">Disbursements to bill on {matterRef}</p>
      {error && <p className="text-sm text-status-red">{error}</p>}
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="disb-only-none">
          Nothing unbilled is logged on this matter. Pick from LawBillity below, or type it in the box for Accounts.
        </p>
      ) : (
        rows.map((d) => (
          <label key={d.id} className="flex items-center gap-3 text-sm cursor-pointer" data-testid="disb-only-row">
            <Checkbox checked={!off.has(d.id)} onCheckedChange={() => toggle(d.id)} />
            <span className="flex-1">{d.description}</span>
            <span className="tabular-nums">£{Number(d.amount).toFixed(2)}</span>
            <span className="text-xs text-gray-500 w-14">{VAT_LABEL[d.vat_treatment] || ""}</span>
          </label>
        ))
      )}
    </div>
  );
}
